import { useState, type ReactNode } from 'react';

import { Estado, data } from '../../comum';
import {
  CORES,
  ROTULOS,
  ordenar,
  rotular,
  type Cartao,
  type DadosCartao,
  type Variacao,
} from './tipos';

/**
 * Os cartões do painel e as formas de desenhar um número.
 *
 * Saíram de `Inicio.tsx` porque não compartilham estado com a tela: recebem o
 * cartão e os dados já carregados. O que os une é a regra de leitura — um
 * gráfico que some com o grupo "(não informado)" apaga justamente as máquinas
 * que ninguém mediu, que são as que mais interessam.
 */
/**
 * Acrescenta à consulta do cartão o filtro de uma fatia do gráfico.
 *
 * A chave vem crua do banco — `win10`, `linux` — e é ela que vai para o
 * filtro, não o rótulo bonito da legenda. O grupo "(não informado)" é o caso
 * que engana: ele representa **ausência de valor**, e filtrar por igualdade
 * com o texto "(não informado)" não acharia máquina nenhuma.
 */
export function comFiltroDeGrupo(
  consulta: Cartao['consulta'],
  chave: string,
): Record<string, unknown> {
  const campo = consulta.agruparPor ?? '';
  const semValor = chave === '(não informado)' || chave === '';

  return {
    ...consulta,
    intencao: 'listar',
    agruparPor: null,
    filtros: [
      ...(consulta.filtros ?? []),
      semValor
        ? { campo, operador: 'nao_existe', valor: null }
        : { campo, operador: 'eq', valor: chave },
    ],
  };
}

/* ── Cartões ─────────────────────────────────────────────────────────────── */

export function CartaoPainel({
  cartao,
  dados,
  aoAbrir,
  aoEscolherGrupo,
  aoRemover,
  aoEditar,
  posicao,
  total,
  aoMover,
  aoSoltar,
  arrastando,
  setArrastando,
}: {
  cartao: Cartao;
  dados: DadosCartao | null;
  aoAbrir: () => void;
  /** Clique numa fatia do gráfico. */
  aoEscolherGrupo?: (chave: string) => void;
  aoRemover?: () => void;
  aoEditar?: () => void;
  posicao?: number;
  total?: number;
  aoMover?: (para: number) => void;
  /** Recebe a posição de DESTINO; quem sabe a origem é o painel. */
  aoSoltar?: (destino: number) => void;
  arrastando?: string | null;
  setArrastando?: (id: string | null) => void;
}): ReactNode {
  const editavel = aoRemover !== undefined;
  const podeMover = aoMover !== undefined && posicao !== undefined && total !== undefined;
  const acende =
    cartao.limiteAlerta !== null &&
    dados !== null &&
    dados.erro === null &&
    dados.total >= cartao.limiteAlerta;

  return (
    <section
      className={
        `cartao-painel largura-${cartao.largura}` +
        (acende ? ' acende' : '') +
        (arrastando === cartao.id ? ' arrastando' : '')
      }
      title={cartao.consulta.explicacao}
      /*
        Arrastar nativo do HTML — sem biblioteca. A instalação do cliente não
        tem internet e a política de conteúdo bloqueia script externo; toda
        dependência de terceiro aqui viraria um problema de empacotamento.
      */
      draggable={podeMover}
      onDragStart={() => setArrastando?.(cartao.id)}
      onDragEnd={() => setArrastando?.(null)}
      onDragOver={(e) => {
        if (podeMover && arrastando !== null && arrastando !== cartao.id) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (posicao !== undefined) aoSoltar?.(posicao);
        setArrastando?.(null);
      }}
    >
      <header>
        <h3>{cartao.titulo}</h3>
        {editavel && (
          <span className="acoes-cartao">
            {/*
              Setas além do arrastar: quem usa teclado, leitor de tela ou toque
              não consegue arrastar. Arrastar é atalho, não a única porta.
            */}
            {podeMover && (
              <>
                <button
                  className="icone-cartao"
                  title="Mover para a esquerda"
                  disabled={posicao === 0}
                  onClick={() => aoMover?.((posicao ?? 0) - 1)}
                >
                  ←
                </button>
                <button
                  className="icone-cartao"
                  title="Mover para a direita"
                  disabled={posicao === (total ?? 1) - 1}
                  onClick={() => aoMover?.((posicao ?? 0) + 1)}
                >
                  →
                </button>
              </>
            )}
            {aoEditar !== undefined && (
              <button className="icone-cartao" title="Editar cartão" onClick={aoEditar}>
                ✎
              </button>
            )}
            {aoRemover !== undefined && (
              <button
                className="icone-cartao remover-cartao"
                title="Remover do painel"
                onClick={aoRemover}
              >
                ✕
              </button>
            )}
          </span>
        )}
      </header>

      {dados === null ? (
        <p className="suave">Carregando…</p>
      ) : dados.erro !== null ? (
        // Erro isolado por cartão: um cartão quebrado não apaga o painel.
        <p className="erro-cartao">Não foi possível calcular: {dados.erro}</p>
      ) : (
        <>
          {cartao.forma === 'numero' && (
            <>
              <div className="linha-numero">
                {/*
                  🔴 Era um `div` com `onClick` e `role="presentation"`: não
                  alcançável por teclado, e o `role` mandava o leitor de tela
                  ignorar. Alvo grande de clique não pode custar o acesso de
                  quem navega sem mouse.
                */}
                <button
                  className="numerao"
                  onClick={aoAbrir}
                  title={`Ver as ${dados.total} máquinas no inventário`}
                >
                  {dados.total}
                </button>
                <Tendencia v={dados.variacao} />
              </div>
              <Serie pontos={dados.serie} />
              {dados.projecao !== null && (
                <p className="projecao" title={`Base: ${dados.projecao.baseDias} dias de medição`}>
                  {dados.projecao.texto}
                </p>
              )}
            </>
          )}
          {cartao.forma === 'barras' && (
            <Barras
              grupos={dados.grupos}
              {...(aoEscolherGrupo === undefined ? {} : { aoEscolher: aoEscolherGrupo })}
            />
          )}
          {cartao.forma === 'rosca' && (
            <Rosca
              grupos={dados.grupos}
              {...(aoEscolherGrupo === undefined ? {} : { aoEscolher: aoEscolherGrupo })}
            />
          )}
          {cartao.forma === 'tabela' && <TabelaCurta itens={dados.itens} />}
        </>
      )}

      {cartao.nota !== null && <p className="nota-cartao">{cartao.nota}</p>}

      {/*
        🔴 O cartão acendia e não dizia por quê.
        Alguém via a borda laranja e não sabia se o limite era 10, se acabou de
        cruzar ou se estava estourado há três semanas. Alerta que chama atenção
        sem entregar o motivo da atenção gasta a atenção à toa.
      */}
      {cartao.limiteAlerta !== null && dados !== null && dados.erro === null && (
        <p className={`limite${acende ? ' estourado' : ''}`}>
          {acende
            ? `Acima do limite de ${cartao.limiteAlerta}`
            : `Acende acima de ${cartao.limiteAlerta}`}
        </p>
      )}

      {/*
        O link vale para toda forma de cartão.
        Antes só o número era clicável — quem via barras ou tabela não tinha
        como chegar às máquinas, e nada na tela dizia que faltava.
      */}
      {dados !== null && dados.erro === null && (
        <button className="link ver-no-inventario" onClick={aoAbrir}>
          Ver no inventário →
        </button>
      )}
    </section>
  );
}

/** Quantos grupos viram barra. Acima disso, a diferença é anunciada. */
const TETO_BARRAS = 8;

export function Barras({
  grupos,
  aoEscolher,
}: {
  grupos: readonly { readonly chave: string; readonly total: number }[];
  /** Clique numa categoria — leva ao inventário só com aquela fatia. */
  aoEscolher?: (chave: string) => void;
}): ReactNode {
  if (grupos.length === 0) return <p className="suave">Sem dados.</p>;
  const lista = ordenar(grupos);
  const maior = Math.max(...lista.map((g) => g.total));
  const total = lista.reduce((s, g) => s + g.total, 0);
  /**
   * 🔴 O total conta TODOS os grupos; só oito viram barra.
   *
   * O rodapé de cada barra dizia "X de {total}" — e as barras visíveis somavam
   * menos que esse total. Quem conferia não fechava a conta e não tinha como
   * saber que havia mais grupos fora do desenho.
   */
  const somaVisivel = lista.slice(0, TETO_BARRAS).reduce((s, g) => s + g.total, 0);

  return (
    <>
      <ul className="barras">
        {lista.slice(0, TETO_BARRAS).map((g, i) => (
          <li key={g.chave} title={`${rotular(g.chave)}: ${g.total} de ${total}`}>
            {aoEscolher === undefined ? (
              <span className="rot">{rotular(g.chave)}</span>
            ) : (
              <button className="link rot" onClick={() => aoEscolher(g.chave)}>
                {rotular(g.chave)}
              </button>
            )}
            <span className="trilho">
              <span
                className="preenche"
                style={{
                  // Proporcional ao MAIOR, não ao total: com uma categoria de 90%
                  // as outras viriam como fios invisíveis.
                  width: `${Math.max(2, (g.total / maior) * 100)}%`,
                  background: CORES[i % CORES.length] ?? CORES[0],
                }}
              />
            </span>
            <span className="num">{g.total}</span>
          </li>
        ))}
      </ul>
      {lista.length > TETO_BARRAS && (
        <p className="corte" role="status">
          Mostrando <strong>{TETO_BARRAS}</strong> de <strong>{lista.length}</strong> grupos (
          <strong>{somaVisivel}</strong> de <strong>{total}</strong>).
        </p>
      )}
    </>
  );
}

/**
 * Rosca em SVG puro.
 *
 * Sem biblioteca de gráfico: são poucos arcos, e uma dependência de charting
 * custaria centenas de kB num pacote que o técnico leva em pendrive para um
 * ambiente fechado.
 */
export function Rosca({
  grupos,
  aoEscolher,
}: {
  grupos: readonly { readonly chave: string; readonly total: number }[];
  /** Clique numa categoria — leva ao inventário só com aquela fatia. */
  aoEscolher?: (chave: string) => void;
}): ReactNode {
  const lista = ordenar(grupos);
  const total = lista.reduce((s, g) => s + g.total, 0);
  if (total === 0) return <p className="suave">Sem dados.</p>;

  const raio = 52;
  const circunferencia = 2 * Math.PI * raio;
  let acumulado = 0;

  return (
    <div className="rosca">
      <svg viewBox="0 0 140 140" width="132" height="132" role="img">
        <g transform="translate(70,70) rotate(-90)">
          {lista.slice(0, 7).map((g, i) => {
            const fracao = g.total / total;
            const traco = fracao * circunferencia;
            const offset = -acumulado * circunferencia;
            acumulado += fracao;
            return (
              <circle
                key={g.chave}
                r={raio}
                fill="none"
                stroke={CORES[i % CORES.length] ?? CORES[0]}
                strokeWidth="22"
                strokeDasharray={`${traco} ${circunferencia - traco}`}
                strokeDashoffset={offset}
              >
                <title>{`${rotular(g.chave)}: ${g.total}`}</title>
              </circle>
            );
          })}
        </g>
        <text x="70" y="66" textAnchor="middle" className="rosca-total">
          {total}
        </text>
        <text x="70" y="84" textAnchor="middle" className="rosca-rot">
          total
        </text>
      </svg>
      {/*
        Fatia clicável.
        Ver "Windows 21" e não poder clicar para ver as 21 é a frustração mais
        previsível de um gráfico. O link do rodapé leva ao inventário inteiro,
        que não era o que a pessoa queria.
      */}
      <ul className="legenda">
        {lista.slice(0, 7).map((g, i) => (
          <li key={g.chave}>
            <i style={{ background: CORES[i % CORES.length] ?? CORES[0] }} />
            {aoEscolher === undefined ? (
              <span className="rot-legenda">{rotular(g.chave)}</span>
            ) : (
              <button
                className="link rot-legenda"
                title={`Ver as ${g.total} máquinas de ${rotular(g.chave)}`}
                onClick={() => aoEscolher(g.chave)}
              >
                {rotular(g.chave)}
              </button>
            )}
            <b>{g.total}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TabelaCurta({ itens }: { itens: readonly Record<string, unknown>[] }): ReactNode {
  if (itens.length === 0) return <p className="suave">Sem resultados.</p>;
  const colunas = Object.keys(itens[0] ?? {}).slice(0, 3);

  return (
    <table className="densa">
      <thead>
        <tr>
          {colunas.map((c) => (
            <th key={c}>{c.replace(/_/g, ' ')}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {itens.map((l, i) => (
          <tr key={i}>
            {colunas.map((c) => (
              <td key={c}>{l[c] === null || l[c] === undefined ? '—' : String(l[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Variação desde a última medição comparável.
 *
 * 🔴 **Nada aparece quando não há como comparar.** Cartão novo, cartão editado
 * ou instalação recente não têm tendência, e um "0%" nesse caso afirmaria uma
 * estabilidade que ninguém mediu. Num painel de conformidade isso é pior que
 * silêncio: alguém decide não agir com base num número que não existe.
 *
 * A cor não julga. Em "máquinas sem reportar" subir é ruim; em "máquinas
 * conformes" subir é ótimo — e o cartão não sabe qual dos dois ele é. Quem
 * julga é o limite de alerta, que a pessoa configurou.
 */
export function Tendencia({ v }: { v: Variacao | null }): ReactNode {
  if (v === null || v.delta === 0) return null;

  const subiu = v.delta > 0;
  return (
    <span
      className="tendencia"
      title={`Era ${v.valorAnterior} há ${v.dias} dia(s). Agora é ${v.valorAnterior + v.delta}.`}
    >
      <span className="seta">{subiu ? '▲' : '▼'}</span>
      {subiu ? '+' : '−'}
      {Math.abs(v.delta)}
      <span className="periodo">em {v.dias}d</span>
    </span>
  );
}

/**
 * Minigráfico da série.
 *
 * `▲+3` conta o último passo; **a linha conta a história**. É a diferença entre
 * "teve um pico ontem" e "está piorando desde o mês passado", e a segunda é a
 * que muda o que alguém faz.
 *
 * Some com menos de três pontos: dois pontos viram uma reta que sugere um rumo
 * que ninguém mediu.
 */
export function Serie({
  pontos,
}: {
  pontos: readonly { readonly valor: number; readonly em: string }[];
}): ReactNode {
  if (pontos.length < 3) return null;

  const valores = pontos.map((p) => p.valor);
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const faixa = max - min || 1;

  const L = 120;
  const A = 26;
  const passo = pontos.length > 1 ? L / (pontos.length - 1) : L;

  const caminho = valores
    .map((v, i) => {
      const x = i * passo;
      // Invertido: no SVG o zero fica em cima.
      const y = A - ((v - min) / faixa) * A;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const primeiro = valores[0] ?? 0;
  const ultimo = valores[valores.length - 1] ?? 0;
  const dias = Math.max(
    1,
    Math.round(
      (new Date(pontos[pontos.length - 1]?.em ?? '').getTime() -
        new Date(pontos[0]?.em ?? '').getTime()) /
        86_400_000,
    ),
  );

  return (
    <svg
      className="serie"
      viewBox={`0 -3 ${L} ${A + 6}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Últimos ${dias} dias: de ${primeiro} para ${ultimo}`}
    >
      <title>{`Últimos ${dias} dias: de ${primeiro} para ${ultimo}`}</title>
      <path d={caminho} fill="none" strokeWidth="1.5" />
      <circle cx={L} cy={A - ((ultimo - min) / faixa) * A} r="2" />
    </svg>
  );
}
